import AIInsightsTabSvg from "@callveriq/components/svg/CI/AIInsightsTabSvg";
import CustomTrackingTabSvg from "@callveriq/components/svg/CI/CustomTrackingTabSvg";
import InsightsTabSvg from "@callveriq/components/svg/CI/InsightsTabSvg";
import AdvancedReportsSvg from "@callveriq/components/svg/Dashboard/AdvancedReportSvg";
import AuditSvg from "@callveriq/components/svg/Dashboard/AuditSvg";
import CollectionSvg from "@callveriq/components/svg/Dashboard/CollectionSvg";
import CsatSvg from "@callveriq/components/svg/Dashboard/CsatSvg";
import CustomerIntentSvg from "@callveriq/components/svg/Dashboard/CustomerIntentSvg";
import CustomerSatisfactionAltSvg from "@callveriq/components/svg/Dashboard/CustomerSatisfactionAltSvg";
import EscalationSvg from "@callveriq/components/svg/Dashboard/EscalationSvg";
import LeadSvg from "@callveriq/components/svg/Dashboard/LeadSvg";
import OverallSvg from "@callveriq/components/svg/Dashboard/OverallSvg";
import ParameterSvg from "@callveriq/components/svg/Dashboard/ParameterSvg";
import RepeatCallsSvg from "@callveriq/components/svg/Dashboard/RepeatCallsSvg";
import RepSvg from "@callveriq/components/svg/Dashboard/RepSvg";
import ReportSvg from "@callveriq/components/svg/Dashboard/ReportSvg";
import TeamSvg from "@callveriq/components/svg/Dashboard/TeamSvg";
import ViolationSvg from "@callveriq/components/svg/Dashboard/ViolationSvg";
import {
    AccountNavSvg,
    AIAgentNavSvg,
    AuditReportNavSvg,
    CoachingSvg,
    ConversationListNavSvg,
    CustomerIntelligenceNavSvg,
    HomeNavSvg,
    LMSNavSvg,
    SalesAssistNavSvg,
    SettingsNavSvg,
    SupervisorAssistNavSvg,
} from "@callveriq/modules/GlobalDashboardLayout/components/NavLinkSvg";
import { MeetingTypeConst } from "@callveriq/type/Common";
import { SxProps, Theme } from "@mui/material";
import React from "react";
import {
    PermissionModules,
    PermissionTypes,
    SupportDashboardPermissionCodeNames,
} from "./permissions.config";
import { CheckCanAccessParams } from "@callveriq/contexts/AuthContext";
import AccuracySvg from "@callveriq/components/svg/Dashboard/AccuracySvg";
import EntitySvg from "@callveriq/components/svg/Dashboard/EntitySvg";

import {
    BulkCallSvg,
    ConversationalPathwaySvg,
    SingleCallSvg,
} from "@callveriq/modules/aiVoiceCall/components/NavSvgIcons";
import {
    DashboardKeys,
    FeatureAccessKeys,
    SettingKeys,
} from "@callveriq/type/Domain";
import DomainInstance from "@callveriq/utils/helper/DomainInstance";

export type RouteType = {
    name: string;
    to: string;
    path: string;
    canAccessName?: string;
    Icon?: React.FC<{ sx?: SxProps<Theme> }>;
    desc?: string;
    hideLink?: boolean;
} & (
    | {
          protectionType?: never;
      }
    | {
          protectionType: "default";
          featureAccessVisibility?: never;
          permission?: never;
          domainVisibility?: never;
      }
    | {
          protectionType: "featureAccess";
          featureAccessVisibility: FeatureAccessKeys;
      }
    | {
          protectionType: "permission";
          permission: CheckCanAccessParams;
      }
    | {
          protectionType: "domainVisibility";
          domainVisibility: DomainInstance["domainType"];
      }
    | {
          protectionType: "domainVisibility & dashboardAccess";
          domainVisibility: DomainInstance["domainType"];
          dashboardAccess: DashboardKeys;
      }
    | {
          protectionType: "permission & featureAccess";
          permission: CheckCanAccessParams;
          featureAccessVisibility: FeatureAccessKeys;
      }
    | {
          protectionType: "permission & dashboardAccess";
          permission: CheckCanAccessParams;
          dashboardAccess: DashboardKeys;
      }
    | {
          protectionType: "permission & settingAccess";
          permission: CheckCanAccessParams;
          settingAccess: SettingKeys;
      }
    | {
          protectionType: "settingAccess";
          settingAccess: SettingKeys;
      }
    | {
          protectionType: "dashboardAccess";
          dashboardAccess: DashboardKeys;
      }
    | {
          protectionType: "permission & domainVisibility";
          permission: CheckCanAccessParams;
          domainVisibility: DomainInstance["domainType"];
      }
    | {
          protectionType: "default & domainVisibility";
          domainVisibility: DomainInstance["domainType"];
      }
    | {
          protectionType: "is_internal";
      }
    | {
          protectionType: "is_internal & anyDashboardAccess";
          dashboardAccessList: DashboardKeys[];
      }
    | {
          /** Visible only to Callveriq internal users (`user.user_type === 2`), never by domain. */
          protectionType: "is_internal_user";
      }
    | {
          protectionType: "default & dashboardAccess";
          dashboardAccess: DashboardKeys;
      }
    | {
          protectionType: "default & settingAccess";
          settingAccess: SettingKeys;
      }
);

type RouteTypeWithSubRoutes = {
    name: string;
    hasSubRoutes: boolean;
    subRoutes: { [key in Paths]?: RouteType };
    protectionType?: RouteType["protectionType"];
    settingAccess?: SettingKeys;
};

type Paths =
    | "USER_MANAGER"
    | "ROLE_MANAGER"
    | "TOPIC_MANAGER"
    | "INTEGRATIONS"
    | "TEAM_MANAGER"
    | "AUDIT_MANAGER"
    | "SAMPLING_MANAGER"
    | "VIOLATION_MANAGER"
    | "RECORDING_MANAGER"
    | "CALL_TYPE_MANAGER"
    | "CALL_TAGS_MANAGER"
    | "AGENT_ASSIST"
    | "ENTITY"
    | "AI_SUMMARY"
    | "MANUAL_QMS"
    | "SCHEDULED_REPORTS"
    | "BILLING"
    | "SCORE_SENSE"
    | "ACPT_ANALYSIS"
    | "AI_INSIGHTS"
    | "SENTIMENT_ANALYSIS"
    | "AI_DISPOSITION"
    | "AI_COACHING"
    | "DASHBOARD_REPORTS"
    | "SUBDOMAIN_CONFIG"
    | "CUSTOM_COLUMN_ADDITION"
    | "SUBSCRIPTION_UPDATE"
    | "TRANSCRIPT_EXTRACTION"
    | "EMAIL_REPORTS"
    | "GPT_REPORT"
    | "EVALS"
    | "MERGE_OPERATIONS"
    | "DOMAIN_CONTEXT"
    | "WIN_LOSS_REPORT"
    | "TAG_CREATION"
    | "GPT_RERUN_FILE"
    | "CALIBRATION_RERUN"
    | "MEETING_AI_AUDIT_RERUN"
    | "MEETING_DATA_PACKET"
    | "MANUAL_UPLOAD";

export const SettingRoutes: Record<Paths, RouteType> = {
    USER_MANAGER: {
        name: "User Manager",
        to: "user_manager",
        path: "user_manager",
        protectionType: "default",
    },
    ROLE_MANAGER: {
        name: "Role Manager",
        to: "role_manager",
        path: "role_manager",
        protectionType: "default",
    },
    TOPIC_MANAGER: {
        name: "Topic Manager",
        to: "topic_manager",
        path: "topic_manager",
        protectionType: "default",
    },
    INTEGRATIONS: {
        name: "Integrations",
        to: "integrations",
        path: "integrations",
        protectionType: "default",
    },

    TEAM_MANAGER: {
        name: "Team Manager",
        to: "team_manager",
        path: "team_manager",
        protectionType: "default",
    },
    AUDIT_MANAGER: {
        name: "Audit Manager",
        to: "audit_manager",
        path: "audit_manager",
        protectionType: "default",
    },
    SAMPLING_MANAGER: {
        name: "Sampling Manager",
        to: "sampling_manager",
        path: "sampling_manager",
        protectionType: "default",
    },
    VIOLATION_MANAGER: {
        name: "Violation Manager",
        to: "violation_manager",
        path: "violation_manager",
        protectionType: "permission",
        permission: {
            heading: PermissionModules.Audit_Manager,
            code_name: "audit.can_manage_violation",
            permission_type: PermissionTypes.edit,
        },
    },
    RECORDING_MANAGER: {
        name: "Recording Manager",
        to: "recording_manager",
        path: "recording_manager",
        protectionType: "default & settingAccess",
        settingAccess: "recording",
    },
    CALL_TYPE_MANAGER: {
        name: "Type Manager",
        canAccessName: "Call Type Manager",
        to: "type_manager",
        path: "type_manager",
        protectionType: "default",
    },
    CALL_TAGS_MANAGER: {
        name: "Tags Manager",
        canAccessName: "Call Tags Manager",
        to: "tags_manager",
        path: "tags_manager",
        protectionType: "default",
    },
    BILLING: {
        name: "Billing",
        to: "billing",
        path: "billing",
        // protectionType: "default",
        protectionType: "settingAccess",
        settingAccess: "billing",
    },
    SCHEDULED_REPORTS: {
        name: "Scheduled Reports",
        to: "scheduled_reports",
        path: "scheduled_reports",
        protectionType: "default",
    },
    ENTITY: {
        name: "Entity",
        to: "entity",
        path: "entity",
        protectionType: "permission & featureAccess",
        featureAccessVisibility: "entity",
        permission: {
            heading: PermissionModules.Gpt_Features,
            code_name: "gpt.view_entity",
            permission_type: PermissionTypes.edit,
        },
    },
    AI_SUMMARY: {
        name: "AI Summary",
        to: "ai_summary",
        path: "ai_summary",
        protectionType: "permission & featureAccess",
        featureAccessVisibility: "summary",
        permission: {
            heading: PermissionModules.Gpt_Features,
            code_name: "gpt.view_custom_summary",
            permission_type: PermissionTypes.edit,
        },
    },
    MANUAL_QMS: {
        name: "Manual QMS",
        to: "qms",
        path: "qms",
        protectionType: "default",
        canAccessName: "QMS",
    },
    AGENT_ASSIST: {
        name: "Agent Assist",
        to: "agent_assist",
        path: "agent_assist",
        protectionType: "permission & featureAccess",
        featureAccessVisibility: "live_assist",
        permission: {
            heading: PermissionModules.Agent_Assist,
            code_name: "live_assist.can_access_agent_assist_settings",
            permission_type: PermissionTypes.edit,
        },
    },
    ACPT_ANALYSIS: {
        name: "ACPT Analysis",
        to: "acpt_analysis",
        path: "acpt_analysis",
        protectionType: "permission & featureAccess",
        featureAccessVisibility: "acpt",
        permission: {
            heading: PermissionModules.Gpt_Features,
            code_name: "gpt.view_acpt",
            permission_type: PermissionTypes.edit,
        },
    },
    SCORE_SENSE: {
        name: "ScoreSense",
        to: "score_sense",
        path: "score_sense",
        canAccessName: "Score Sense",
        protectionType: "permission",
        permission: {
            heading: PermissionModules.Gpt_Features,
            code_name: "gpt.view_score_sense",
            permission_type: PermissionTypes.edit,
        },
    },
    AI_INSIGHTS: {
        name: "AI Insights",
        to: "ai_insights",
        path: "ai_insights",
        protectionType: "permission & featureAccess",
        featureAccessVisibility: "ai_feedback",
        permission: {
            heading: PermissionModules.Gpt_Features,
            code_name: "gpt.ai_feedback",
            permission_type: PermissionTypes.edit,
        },
    },
    SENTIMENT_ANALYSIS: {
        name: "Sentiment Analysis",
        to: "sentiment_analysis",
        path: "sentiment_analysis",
        protectionType: "permission & featureAccess",
        featureAccessVisibility: "gpt_sentiment_analysis",
        permission: {
            heading: PermissionModules.Gpt_Features,
            code_name: "gpt.view_sentiment_analysis",
            permission_type: PermissionTypes.edit,
        },
    },
    AI_DISPOSITION: {
        name: "AI Disposition",
        to: "ai_disposition",
        path: "ai_disposition",
        protectionType: "permission & featureAccess",
        featureAccessVisibility: "ai_disposition",
        permission: {
            heading: PermissionModules.Gpt_Features,
            code_name: "gpt.view_ai_disposition",
            permission_type: PermissionTypes.edit,
        },
    },
    AI_COACHING: {
        name: "AI Coaching",
        to: "ai_coaching",
        path: "ai_coaching",
        protectionType: "permission & featureAccess",
        canAccessName: "Coaching",
        featureAccessVisibility: "coaching",
        permission: {
            heading: PermissionModules.Coaching,
            code_name: "coachings.settings.view_edit",
            permission_type: PermissionTypes.edit,
        },
    },
    DASHBOARD_REPORTS: {
        name: "LMD - Support",
        to: "dashboard_reports",
        path: "dashboard_reports",
        protectionType: "is_internal & anyDashboardAccess",
        dashboardAccessList: [
            "customer_repeat_call",
            "customer_escalation",
            "customer_intent",
            "customer_satisfaction",
        ],
    },
    SUBDOMAIN_CONFIG: {
        name: "Subdomain Config",
        to: "subdomain_config",
        path: "subdomain_config",
        protectionType: "is_internal_user",
    },
    /* Internal tools migrated from Retool: internal users only, current domain only. */
    CUSTOM_COLUMN_ADDITION: {
        name: "Custom Column Addition",
        to: "custom_column_addition",
        path: "custom_column_addition",
        protectionType: "is_internal_user",
    },
    SUBSCRIPTION_UPDATE: {
        name: "Subscription Update",
        to: "subscription_update",
        path: "subscription_update",
        protectionType: "is_internal_user",
    },
    TRANSCRIPT_EXTRACTION: {
        name: "Transcript Extraction",
        to: "transcript_extraction",
        path: "transcript_extraction",
        protectionType: "is_internal_user",
    },
    EMAIL_REPORTS: {
        name: "Email Reports",
        to: "internal_email_reports",
        path: "internal_email_reports",
        protectionType: "is_internal_user",
    },
    GPT_REPORT: {
        name: "GPT Report",
        to: "gpt_report",
        path: "gpt_report",
        protectionType: "is_internal_user",
    },
    EVALS: {
        name: "Evals",
        to: "gen_ai_evals",
        path: "gen_ai_evals",
        protectionType: "is_internal_user",
    },
    MERGE_OPERATIONS: {
        name: "Merge Operations",
        to: "merge_operations",
        path: "merge_operations",
        protectionType: "is_internal_user",
    },
    DOMAIN_CONTEXT: {
        name: "Domain Context",
        to: "domain_context",
        path: "domain_context",
        protectionType: "is_internal_user",
    },
    WIN_LOSS_REPORT: {
        name: "Win/Loss Report",
        to: "win_loss_report",
        path: "win_loss_report",
        protectionType: "is_internal_user",
    },
    TAG_CREATION: {
        name: "Tag Creation",
        to: "bulk_tag_creation",
        path: "bulk_tag_creation",
        protectionType: "is_internal_user",
    },
    GPT_RERUN_FILE: {
        name: "Re-run GPT (File)",
        to: "gpt_rerun_file",
        path: "gpt_rerun_file",
        protectionType: "is_internal_user",
    },
    CALIBRATION_RERUN: {
        name: "Calibration Re-run",
        to: "calibration_rerun",
        path: "calibration_rerun",
        protectionType: "is_internal_user",
    },
    MEETING_AI_AUDIT_RERUN: {
        name: "Meeting AI Audit Re-run",
        to: "meeting_ai_audit_rerun",
        path: "meeting_ai_audit_rerun",
        protectionType: "is_internal_user",
    },
    MEETING_DATA_PACKET: {
        name: "Extract Meeting Data Packet",
        to: "meeting_data_packet",
        path: "meeting_data_packet",
        protectionType: "is_internal_user",
    },
    MANUAL_UPLOAD: {
        name: "Manual Call & Audit Upload",
        to: "manual_call_audit_upload",
        path: "manual_call_audit_upload",
        protectionType: "is_internal_user",
    },
};

/**
 * Settings -> Internal Tools pages migrated from Retool, in sidebar order
 * (after Subdomain Config). Each is gated by `is_internal_user`.
 */
export const INTERNAL_TOOL_SETTING_ROUTE_KEYS = [
    "CUSTOM_COLUMN_ADDITION",
    "SUBSCRIPTION_UPDATE",
    "TRANSCRIPT_EXTRACTION",
    "EMAIL_REPORTS",
    "GPT_REPORT",
    "EVALS",
    "MERGE_OPERATIONS",
    "DOMAIN_CONTEXT",
    "WIN_LOSS_REPORT",
    "TAG_CREATION",
    "GPT_RERUN_FILE",
    "CALIBRATION_RERUN",
    "MEETING_AI_AUDIT_RERUN",
    "MEETING_DATA_PACKET",
    "MANUAL_UPLOAD",
] as const satisfies readonly Paths[];

export const SettingsTypeConfig: Array<RouteTypeWithSubRoutes> = [
    {
        name: "General",
        hasSubRoutes: true,
        subRoutes: {
            TEAM_MANAGER: SettingRoutes.TEAM_MANAGER,
            ROLE_MANAGER: SettingRoutes.ROLE_MANAGER,
            USER_MANAGER: SettingRoutes.USER_MANAGER,
            RECORDING_MANAGER: SettingRoutes.RECORDING_MANAGER,
        },
    },
    {
        name: "Integrations & Apps",
        hasSubRoutes: true,
        protectionType: "settingAccess",
        settingAccess: "integration_app",
        subRoutes: {
            INTEGRATIONS: SettingRoutes.INTEGRATIONS,
            AGENT_ASSIST: SettingRoutes.AGENT_ASSIST,
        },
    },
    {
        name: "Product Settings",
        hasSubRoutes: true,
        protectionType: "settingAccess",
        settingAccess: "product",
        subRoutes: {
            AUDIT_MANAGER: SettingRoutes.AUDIT_MANAGER,
            SAMPLING_MANAGER: SettingRoutes.SAMPLING_MANAGER,
            VIOLATION_MANAGER: SettingRoutes.VIOLATION_MANAGER,
            CALL_TYPE_MANAGER: SettingRoutes.CALL_TYPE_MANAGER,
            CALL_TAGS_MANAGER: SettingRoutes.CALL_TAGS_MANAGER,
            TOPIC_MANAGER: SettingRoutes.TOPIC_MANAGER,
            MANUAL_QMS: SettingRoutes.MANUAL_QMS,
            SCHEDULED_REPORTS: SettingRoutes.SCHEDULED_REPORTS,
            AI_COACHING: SettingRoutes.AI_COACHING,
        },
    },
    {
        name: "CallveriqGPT Settings",
        hasSubRoutes: true,
        subRoutes: {
            AI_DISPOSITION: SettingRoutes.AI_DISPOSITION,
            AI_SUMMARY: SettingRoutes.AI_SUMMARY,
            ENTITY: SettingRoutes.ENTITY,
            SCORE_SENSE: SettingRoutes.SCORE_SENSE,
            ACPT_ANALYSIS: SettingRoutes.ACPT_ANALYSIS,
            AI_INSIGHTS: SettingRoutes.AI_INSIGHTS,
            SENTIMENT_ANALYSIS: SettingRoutes.SENTIMENT_ANALYSIS,
        },
    },
    {
        name: "Billing",
        hasSubRoutes: false,
        protectionType: "settingAccess",
        settingAccess: "billing",
        subRoutes: {
            BILLING: SettingRoutes.BILLING,
        },
    },
    {
        name: "Internal Tools",
        hasSubRoutes: true,
        subRoutes: {
            DASHBOARD_REPORTS: SettingRoutes.DASHBOARD_REPORTS,
            SUBDOMAIN_CONFIG: SettingRoutes.SUBDOMAIN_CONFIG,
            CUSTOM_COLUMN_ADDITION: SettingRoutes.CUSTOM_COLUMN_ADDITION,
            SUBSCRIPTION_UPDATE: SettingRoutes.SUBSCRIPTION_UPDATE,
            TRANSCRIPT_EXTRACTION: SettingRoutes.TRANSCRIPT_EXTRACTION,
            EMAIL_REPORTS: SettingRoutes.EMAIL_REPORTS,
            GPT_REPORT: SettingRoutes.GPT_REPORT,
            EVALS: SettingRoutes.EVALS,
            MERGE_OPERATIONS: SettingRoutes.MERGE_OPERATIONS,
            DOMAIN_CONTEXT: SettingRoutes.DOMAIN_CONTEXT,
            WIN_LOSS_REPORT: SettingRoutes.WIN_LOSS_REPORT,
            TAG_CREATION: SettingRoutes.TAG_CREATION,
            GPT_RERUN_FILE: SettingRoutes.GPT_RERUN_FILE,
            CALIBRATION_RERUN: SettingRoutes.CALIBRATION_RERUN,
            MEETING_AI_AUDIT_RERUN: SettingRoutes.MEETING_AI_AUDIT_RERUN,
            MEETING_DATA_PACKET: SettingRoutes.MEETING_DATA_PACKET,
            MANUAL_UPLOAD: SettingRoutes.MANUAL_UPLOAD,
        },
    },
];

export const DashboardRoutesConfig: {
    LMS: RouteType & {
        subRoutes: {
            knowledgeBase: RouteType;
            document: RouteType;
            assesments: RouteType;
            questionBank: RouteType;
            generateWithAI: RouteType;
        };
    };
    CI: RouteType & {
        subRoutes: {
            custom_tracking: RouteType;
            insights: RouteType;
            ai_insights: RouteType;
        };
    };
    Home: RouteType & {
        subRoutes: {
            overall_dashboard: RouteType;
            entity_dashboard: RouteType;
            team_level_dashboard: RouteType;
            multi_team_level_dashboard: RouteType;
            rep_level_dashboard: RouteType;
            parameter_dashboard: RouteType;
            violation_dashboard: RouteType;
            audit_dashboard: RouteType;
            accuracy_dashboard: RouteType;
            all_reports: RouteType;
            custom_dashboard: RouteType;
            lead_dashboard: RouteType;
            csat_dashboard: RouteType;
            collection_dashboard: RouteType;
            repeat_calls_dashboard: RouteType;
            escalation_dashboard: RouteType;
            customer_intent_dashboard: RouteType;
            customer_satisfaction_dashboard: RouteType;
            advanced_reports: RouteType;
        };
    };
    Coaching: RouteType & {
        subRoutes: {
            Tasks: RouteType;
            Stats: RouteType & {
                subRoutes: {
                    leaderboard: RouteType;
                    agent_tasks: RouteType;
                };
            };
            AssignedSessions: RouteType;
        };
    };
    Account: RouteType;
    Settings: RouteType;
    Conversations: RouteType;
    SupervisorAssist: RouteType;
    SalesAssist: RouteType;
    AuditReport: RouteType;
    AIAgent: RouteType & {
        subRoutes: {
            SINGLE_CALL: RouteType;
            BULK_CALL: RouteType;
            CONVERSATIONAL_PATHWAYS: RouteType;
        };
    };
    SalesAgent: RouteType;
    ActivityLog: RouteType;
    UserProfile: RouteType;

    /* Pre Auth Routes */
    Signin: RouteType;
    Signup: RouteType;
    GenerateResetPasswordLink: RouteType;
    ResetPassword: RouteType;
    VerifyOtp: RouteType;
    InternalSignin: RouteType;
    CreateDomain: RouteType;
    FindDomain: RouteType;
    Configuration: RouteType;
    LiveConversation: RouteType;
    Share: RouteType;
} = {
    Home: {
        name: "Home",
        to: "/home",
        path: "home",
        Icon: HomeNavSvg,
        protectionType: "domainVisibility & dashboardAccess",
        domainVisibility: "b2c",
        dashboardAccess: "analytics",
        subRoutes: {
            overall_dashboard: {
                name: "Overall Dashboard",
                to: "/home/analytics",
                path: "analytics",
                desc: "Get an overview of the performance of your various teams and reps in your organisation",
                Icon: OverallSvg,
                protectionType: "permission & dashboardAccess",
                dashboardAccess: "overall",
                permission: {
                    heading: PermissionModules.Analytics_Dashboard,
                    code_name: "analytics.manage_analytics_dashboard",
                    permission_type: PermissionTypes.view,
                },
            },
            multi_team_level_dashboard: {
                name: "Multi Team Dashboard",
                to: "/home/multi_team_dashboard",
                path: "multi_team_dashboard",
                desc: "",
                hideLink: true,
                protectionType: "default",
            },
            team_level_dashboard: {
                name: "Team Level Dashboard",
                to: "/home/team_level_dashboard",
                path: "team_level_dashboard",
                desc: "Dive deep in the team level performance and get insights on top and bottom performers",
                Icon: TeamSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "team",
            },
            rep_level_dashboard: {
                name: "Rep Level Dashboard",
                to: "/home/rep_level_dashboard",
                path: "rep_level_dashboard",
                desc: "Understand the performance metrics for a particular agent and take improvement steps",
                Icon: RepSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "rep",
            },

            parameter_dashboard: {
                name: "Parameter Dashboard",
                to: "/home/parameter_level_dashboard",
                path: "parameter_level_dashboard",
                desc: "Unlock data-driven parameter level insights and improve agent performance effortlessly",
                Icon: ParameterSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "parameter",
            },

            violation_dashboard: {
                name: "Violation Dashboard",
                to: "/home/violation_dashboard",
                path: "violation_dashboard",
                desc: "Efficiently monitor and address compliance issues and effectively take remedial measures",
                Icon: ViolationSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "violation",
            },
            audit_dashboard: {
                name: "Audit Dashboard",
                to: "/home/audit_report",
                path: "audit_report",
                desc: "Analyze the Audit teams performance and get an overview of all the manual audits",
                Icon: AuditSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "audit",
            },

            accuracy_dashboard: {
                name: "Accuracy Dashboard",
                to: "/home/accuracy_dashboard",
                path: "accuracy_dashboard",
                desc: "Evaluate accuracy of AI scoring prediction by effectively comparing with manual audits.",
                Icon: AccuracySvg,
                protectionType: "permission & dashboardAccess",
                dashboardAccess: "accuracy",
                permission: {
                    heading: PermissionModules.Accuracy_Dashboard,
                    code_name: "analytics.manage_accuracy_dashboard",
                    permission_type: PermissionTypes.view,
                },
            },
            entity_dashboard: {
                name: "Entity Dashboard",
                to: "/home/entity_dashboard",
                path: "entity_dashboard",
                desc: "Track and analyze entity-level insights across your organization",
                Icon: EntitySvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "entity",
            },
            all_reports: {
                name: "All Reports",
                to: "/home/reports",
                path: "reports",
                desc: "Track and analyze your data with our intuitive reporting section and take informed decisions",
                Icon: ReportSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "all_reports",
            },
            lead_dashboard: {
                name: "Lead Interest Dashboard",
                to: "/home/lead_interest_dashboard",
                path: "lead_interest_dashboard",
                desc: "Identify high-potential leads easily using our dashboard and take right business decisions",
                Icon: LeadSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "lead",
            },
            csat_dashboard: {
                name: "CSAT Dashboard",
                to: "/home/csat_dashboard",
                path: "csat_dashboard",
                desc: "Gain rich insights into customer satisfaction helping you enhance customer experience",
                Icon: CsatSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "csat",
            },
            collection_dashboard: {
                name: "Collection Dashboard",
                to: "/home/collection_dashboard",
                path: "collection_dashboard",
                desc: "Effortlessly manage collections and enhance financial strategies with our insightful reports",
                Icon: CollectionSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "collection",
            },
            repeat_calls_dashboard: {
                name: "Repeat Calls Dashboard",
                to: "/home/repeat_calls_dashboard",
                path: "repeat_calls_dashboard",
                desc: "Track repeat-call trends, top repeat reasons and team-level impact to reduce repeat contact rate",
                Icon: RepeatCallsSvg,
                protectionType: "permission & dashboardAccess",
                dashboardAccess: "customer_repeat_call",
                permission: {
                    heading: PermissionModules.Support_Dashboard,
                    code_name: SupportDashboardPermissionCodeNames.RepeatCalls,
                    permission_type: PermissionTypes.view,
                },
            },
            escalation_dashboard: {
                name: "Escalation Dashboard",
                to: "/home/escalation_dashboard",
                path: "escalation_dashboard",
                desc: "Monitor internal vs external escalation patterns, root causes and business impact",
                Icon: EscalationSvg,
                protectionType: "permission & dashboardAccess",
                dashboardAccess: "customer_escalation",
                permission: {
                    heading: PermissionModules.Support_Dashboard,
                    code_name: SupportDashboardPermissionCodeNames.Escalation,
                    permission_type: PermissionTypes.view,
                },
            },
            customer_intent_dashboard: {
                name: "Customer Intent Dashboard",
                to: "/home/customer_intent_dashboard",
                path: "customer_intent_dashboard",
                desc: "Understand the top call drivers, product mentions and competitor signals across conversations",
                Icon: CustomerIntentSvg,
                protectionType: "permission & dashboardAccess",
                dashboardAccess: "customer_intent",
                permission: {
                    heading: PermissionModules.Support_Dashboard,
                    code_name:
                        SupportDashboardPermissionCodeNames.CustomerIntent,
                    permission_type: PermissionTypes.view,
                },
            },
            customer_satisfaction_dashboard: {
                name: "Customer Experience Dashboard",
                to: "/home/customer_satisfaction_dashboard",
                path: "customer_satisfaction_dashboard",
                desc: "Surface satisfaction & dissatisfaction drivers and their financial impact for every team",
                Icon: CustomerSatisfactionAltSvg,
                protectionType: "permission & dashboardAccess",
                dashboardAccess: "customer_satisfaction",
                permission: {
                    heading: PermissionModules.Support_Dashboard,
                    code_name:
                        SupportDashboardPermissionCodeNames.CustomerSatisfaction,
                    permission_type: PermissionTypes.view,
                },
            },

            custom_dashboard: {
                name: "Custom Dashboard",
                to: "/home/custom_dashboard",
                path: "custom_dashboard",
                desc: "Create Custom Dashboard and charts as per your business requirements",
                Icon: ReportSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "custom",
            },

            advanced_reports: {
                name: "Advanced Reports",
                to: "",
                path: "",
                desc: "Use our advanced reporting dashboard to create customized reporting for your teams",
                Icon: AdvancedReportsSvg,
                protectionType: "permission & dashboardAccess",
                dashboardAccess: "superset",
                permission: {
                    heading: PermissionModules.Reports,
                    code_name: "analytics.can_access_advance_report",
                    permission_type: PermissionTypes.edit,
                },
            },
        },
    },
    Coaching: {
        name: "Coaching Dashboard",
        canAccessName: "Coaching",
        to: "/coaching",
        path: "coaching",
        Icon: CoachingSvg,
        protectionType: "permission & dashboardAccess",
        permission: {
            heading: PermissionModules.Coaching,
        },
        dashboardAccess: "coaching",

        subRoutes: {
            Tasks: {
                name: "Tasks",
                to: "/coaching/tasks",
                path: "tasks",
            },
            Stats: {
                name: "Stats",
                to: "/coaching/stats",
                path: "stats",
                subRoutes: {
                    leaderboard: {
                        name: "Leaderboard",
                        to: "/coaching/stats/leaderboard",
                        path: "leaderboard",
                    },
                    agent_tasks: {
                        name: "Agent Tasks",
                        to: "/coaching/stats/agent_tasks",
                        path: "agent_tasks",
                    },
                },
            },
            AssignedSessions: {
                name: "Assigned Sessions",
                to: "/coaching/assigned_sessions",
                path: "assigned_sessions",
            },
        },
    },
    LMS: {
        name: "LMS",
        to: "/lms",
        path: "lms",
        canAccessName: "Library",
        Icon: LMSNavSvg,
        subRoutes: {
            knowledgeBase: {
                name: "Knowledge Base",
                to: "/lms/knowledge_base",
                path: "knowledge_base",
                protectionType: "dashboardAccess",
                dashboardAccess: "lms_knowledge_base",
            },
            document: {
                name: "Document",
                to: "/lms/document",
                path: "document",
                protectionType: "dashboardAccess",
                dashboardAccess: "lms_knowledge_base",
            },
            assesments: {
                name: "Assessments",
                to: "/lms/assessments",
                path: "assessments",
                protectionType: "dashboardAccess",
                dashboardAccess: "lms_assessment",
            },
            questionBank: {
                name: "Question Bank",
                to: "/lms/assessments/question-bank",
                path: "question-bank",
            },
            generateWithAI: {
                name: "Generate with AI",
                to: "/lms/assessments/generate-with-ai",
                path: "assessments/generate-with-ai",
            },
        },
        protectionType: "default",
    },
    CI: {
        name: "Callveriq Insights",
        to: "/ci_dashboard",
        path: "ci_dashboard",
        Icon: CustomerIntelligenceNavSvg,
        canAccessName: "Callveriq Insights",
        protectionType: "default",

        subRoutes: {
            insights: {
                name: "Customer Intelligence",
                to: "/ci_dashboard/customer_intelligence",
                path: "customer_intelligence",
                Icon: InsightsTabSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "customer_intelligence",
            },
            custom_tracking: {
                name: "Custom Tracking",
                to: "/ci_dashboard/custom_tracking",
                path: "custom_tracking",
                Icon: CustomTrackingTabSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "custom_tracking",
            },
            ai_insights: {
                name: "AI Insights",
                to: "/ci_dashboard/ai_insights",
                path: "ai_insights",
                Icon: AIInsightsTabSvg,
                protectionType: "dashboardAccess",
                dashboardAccess: "ai_insights",
            },
        },
    },
    Account: {
        name: "Accounts",
        to: "/accounts",
        path: "accounts",
        Icon: AccountNavSvg,
        protectionType: "default & dashboardAccess",
        dashboardAccess: "account",
    },
    Settings: {
        name: "Settings",
        to: "/settings",
        path: "settings",
        Icon: SettingsNavSvg,
    },
    Conversations: {
        name: "Conversations",
        to: "/calls",
        path: "calls",
        Icon: ConversationListNavSvg,
        protectionType: "dashboardAccess",
        dashboardAccess: "meeting",
    },
    SupervisorAssist: {
        name: "Supervisor Assist",
        to: "/supervisor_assist",
        path: "supervisor_assist",
        Icon: SupervisorAssistNavSvg,
        protectionType: "permission & featureAccess",
        featureAccessVisibility: "live_assist",
        permission: {
            heading: PermissionModules.Agent_Assist,
            code_name: "live_assist.can_access_supervisor_assist",
            permission_type: PermissionTypes.edit,
        },
    },
    SalesAssist: {
        name: "Live Call",
        to: "/live_call",
        path: "live_call",
        Icon: SalesAssistNavSvg,
        featureAccessVisibility: "saleassist",
        protectionType: "featureAccess",
    },
    AuditReport: {
        name: "Audit Report",
        to: "/audit_report",
        path: "audit_report",
        Icon: AuditReportNavSvg,
        protectionType: "permission & domainVisibility",
        domainVisibility: "b2b",
        permission: {
            heading: PermissionModules.Audit_Manager,
            code_name: "audit.can_audit",
            permission_type: PermissionTypes.edit,
        },
    },
    SalesAgent: {
        name: "AI Agent",
        to: "/ai_agent",
        path: "ai_agent",
        Icon: AIAgentNavSvg,
        featureAccessVisibility: "sale_assist",
        protectionType: "featureAccess",
    },
    AIAgent: {
        name: "AI Agent",
        to: "/ai_voice_call",
        path: "ai_voice_call",
        Icon: AIAgentNavSvg,
        featureAccessVisibility: "ai_agent",
        canAccessName: PermissionModules.AI_Agent,
        permission: {
            heading: PermissionModules.AI_Agent,
            code_name: "voice_bot.single_call_ai_agent",
            permission_type: PermissionTypes.edit,
        },
        protectionType: "permission & featureAccess",
        subRoutes: {
            SINGLE_CALL: {
                name: "Single Call",
                to: "/ai_voice_call/single_call/",
                path: "single_call",
                Icon: SingleCallSvg,
            },
            BULK_CALL: {
                name: "Bulk Call",
                to: "/ai_voice_call/bulk_call/",
                path: "bulk_call",
                Icon: BulkCallSvg,
            },
            CONVERSATIONAL_PATHWAYS: {
                name: "Conversational Pathways",
                to: "/ai_voice_call/conversational_pathways",
                path: "conversational_pathways",
                Icon: ConversationalPathwaySvg,
            },
        },
    },
    LiveConversation: {
        name: "Live Conversation",
        to: "live_conversation",
        path: "live_conversation",
        protectionType: "permission & dashboardAccess",
        dashboardAccess: "live_assist",
        permission: {
            heading: PermissionModules.Agent_Assist,
            code_name: "live_assist.can_access_supervisor_assist",
            permission_type: PermissionTypes.edit,
        },
    },

    /*Pre Auth Routes */
    Signin: {
        name: "Signin",
        to: "/signin",
        path: "signin",
    },
    Signup: {
        name: "Signup",
        to: "/signup",
        path: "signup",
    },
    GenerateResetPasswordLink: {
        name: "Generate Reset Password Link",
        to: "/generate_reset_password_link",
        path: "generate_reset_password_link",
    },
    ResetPassword: {
        name: "Reset Password",
        to: "/resetpassword",
        path: "resetpassword",
    },
    VerifyOtp: {
        name: "Verify OTP",
        to: "/verify_otp",
        path: "verify_otp",
    },
    CreateDomain: {
        name: "Create Domain",
        to: "/",
        path: "",
    },
    FindDomain: {
        name: "Find Domain",
        to: "/find_my_domain",
        path: "find_my_domain",
    },
    InternalSignin: {
        name: "Internal Signin",
        to: "/internal_signin",
        path: "internal_signin",
    },
    ActivityLog: {
        name: "Activity Log",
        to: "/activity_log",
        path: "activity_log",
    },
    UserProfile: {
        name: "User Profile",
        to: "/user_profile",
        path: "user_profile",
    },
    Configuration: {
        name: "Configuration",
        to: "/cofu",
        path: "cofu",
    },
    Share: {
        name: "Share",
        to: "/share",
        path: "share",
    },
};

export const conversationPathList: Array<`${NonNullable<MeetingTypeConst>}`> = [
    "call",
    "chat",
    "email",
];

// Matched with `location.pathname.includes(route)`. Keep the audit_manager
// entries first: AuditManagerCalibration reads `internalRoutes[1]`.
export const internalRoutes: string[] = [
    `${DashboardRoutesConfig.Settings.path}/${SettingRoutes.AUDIT_MANAGER.path}/coaching`,
    `${DashboardRoutesConfig.Settings.path}/${SettingRoutes.AUDIT_MANAGER.path}/filters`,
    // Settings -> Internal Tools pages (shows and polls the Internal notifications tab).
    ...INTERNAL_TOOL_SETTING_ROUTE_KEYS.map(
        (key) =>
            `${DashboardRoutesConfig.Settings.path}/${SettingRoutes[key].path}`
    ),
];
